import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowLeft, Instagram, Award, Users, Link as LinkIcon } from "lucide-react";
import SocialShareButtons from "../components/SocialShareButtons";
import { readSsgInitialData } from "../hooks/useSsgInitialData";

const BASE_URL = "https://allwhufc.com";

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
  </svg>
);

const PlayerFallback = ({ className }) => (
  <div className={`${className} flex items-center justify-center bg-gradient-to-br from-[#7C2C3B] to-[#63232F]`}>
    <svg viewBox="0 0 100 100" className="w-24 h-24 opacity-70">
      <circle cx="50" cy="25" r="15" fill="#F5D76E" />
      <ellipse cx="50" cy="70" rx="25" ry="30" fill="#F5D76E" />
      <g transform="translate(30, 45) scale(0.4)">
        <rect x="10" y="30" width="80" height="12" rx="2" fill="#2BAEE5" transform="rotate(-45 50 36)" />
        <rect x="10" y="30" width="80" height="12" rx="2" fill="#2BAEE5" transform="rotate(45 50 36)" />
      </g>
    </svg>
  </div>
);

export default function PlayerProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  // Hydrate synchronously from the baked SSG shell so JS-rendering
  // Googlebot never sees an empty spinner (would trigger Soft 404).
  const initial = readSsgInitialData(slug);
  const [player, setPlayer] = useState(initial && initial.name ? initial : null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(!(initial && initial.name));
  const [notFound, setNotFound] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    let alive = true;
    // Reset state on slug change so stale prior-slug content never bleeds
    // into a new URL (would cause wrong canonical + duplicate content).
    const fresh = readSsgInitialData(slug);
    setPlayer(fresh && fresh.name ? fresh : null);
    setLoading(!(fresh && fresh.name));
    setNotFound(false);
    setImgError(false);
    setRelated([]);
    axios
      .get(`${API}/players/by-slug/${slug}`)
      .then((res) => {
        if (alive) {
          setPlayer(res.data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!alive) return;
        // Only real 404 → notFound. Other errors (5xx / network) keep
        // whatever content we already hydrated from the shell.
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
        setLoading(false);
      });
    axios
      .get(`${API}/players/${slug}/related?limit=8`)
      .then((res) => alive && setRelated(res.data?.related || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading || (!player && !notFound)) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <SEO title="Loading..." path={`/players/${slug}`} />
        <div className="flex items-center justify-center py-40">
          <div className="spinner" />
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="player-profile-notfound">
        <Navbar />
        <SEO title="Player not found" path={`/players/${slug}`} noindex={true} />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-700 mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            PLAYER NOT FOUND
          </h1>
          <p className="text-slate-500 mb-6">This player profile is unavailable.</p>
          <Link to="/players" className="inline-flex items-center gap-2 text-[#2BAEE5] font-medium hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to all players
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const pos = player.positions?.[0] || player.position || "Player";
  const years = player.years_active || player.years_played || "Historical";
  const ratio = player.appearances > 0 ? (player.goals / player.appearances).toFixed(2) : null;
  const description =
    (player.biography || player.career_summary || `${player.name} — West Ham United ${pos}. ${player.appearances || 0} appearances, ${player.goals || 0} goals. Years at club: ${years}.`)
      .slice(0, 160)
      .replace(/\s+/g, " ")
      .trim();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: player.name,
    jobTitle: pos,
    description: description,
    image: player.image_url || undefined,
    url: `${BASE_URL}/players/${player.slug}`,
    affiliation: {
      "@type": "SportsTeam",
      name: player.team || "West Ham United",
    },
    mainEntityOfPage: `${BASE_URL}/players/${player.slug}`,
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="player-profile-page">
      <SEO
        title={`${player.name} — West Ham United ${pos}`}
        path={`/players/${player.slug}`}
        description={description}
        type="profile"
        image={player.image_url}
        jsonLd={jsonLd}
        seoTitle={player.seo_title}
        seoDescription={player.seo_description}
        keywords={player.seo_keywords}
        imageAlt={player.image_alt}
      />
      <Navbar />

      <div className="bg-[#7C2C3B] py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-4"
            data-testid="player-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-[#F8D742] uppercase tracking-wide">
              {player.team || "West Ham United"}
            </span>
            {player.is_current_squad && (
              <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded font-medium">
                Current squad
              </span>
            )}
          </div>
          <h1
            className="text-4xl md:text-5xl font-bold text-white"
            style={{ fontFamily: "Barlow Condensed" }}
            data-testid="player-name-heading"
          >
            {player.name?.toUpperCase()}
          </h1>
          <p className="text-white/80 mt-1">
            {pos} · {years}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              {player.image_url && !imgError ? (
                <img
                  src={player.image_url}
                  alt={player.name}
                  className="w-full h-80 object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <PlayerFallback className="w-full h-80" />
              )}
            </div>

            {(player.instagram || player.tiktok) && (
              <div className="mt-4 flex gap-3">
                {player.instagram && (
                  <a
                    href={`https://instagram.com/${player.instagram}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white rounded-lg hover:opacity-90 transition-opacity"
                    data-testid="player-instagram-link"
                  >
                    <Instagram className="w-5 h-5" />
                    <span className="text-sm font-medium">@{player.instagram}</span>
                  </a>
                )}
                {player.tiktok && (
                  <a
                    href={`https://tiktok.com/@${player.tiktok}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                    data-testid="player-tiktok-link"
                  >
                    <TikTokIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">@{player.tiktok}</span>
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-[#2BAEE5] to-[#1a8fc4] rounded-xl p-4 text-white text-center">
                <div className="text-4xl font-bold" style={{ fontFamily: "Barlow Condensed" }}>
                  {player.appearances || 0}
                </div>
                <div className="text-sm text-white/80 font-medium uppercase tracking-wide">Games</div>
              </div>
              <div className="bg-gradient-to-br from-[#7C2C3B] to-[#5a1f2b] rounded-xl p-4 text-white text-center">
                <div className="text-4xl font-bold" style={{ fontFamily: "Barlow Condensed" }}>
                  {player.goals || 0}
                </div>
                <div className="text-sm text-white/80 font-medium uppercase tracking-wide">Goals</div>
              </div>
            </div>
            {ratio && (
              <div className="bg-slate-100 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "Barlow Condensed" }}>
                  {ratio}
                </div>
                <div className="text-xs text-slate-500 uppercase">Goals per game</div>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Years at Club</h4>
              <p className="text-lg font-bold text-[#0F172A]">{years}</p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Positions</h4>
              <div className="flex flex-wrap gap-2 mt-1">
                {player.positions?.length > 0 ? (
                  player.positions.map((p, idx) => (
                    <span key={idx} className="bg-[#7C2C3B] text-white text-xs px-3 py-1 rounded-full font-medium">
                      {p}
                    </span>
                  ))
                ) : (
                  <span className="text-slate-500 text-sm">Not specified</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {(player.biography || player.career_summary) && (
          <section className="mt-8 bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-[#7C2C3B]" />
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Barlow Condensed" }}>
                CAREER SUMMARY
              </h2>
            </div>
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
              {player.biography || player.career_summary}
            </p>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-8" data-testid="related-players-section">
            <div className="flex items-center gap-2 mb-4">
              <LinkIcon className="w-5 h-5 text-[#7C2C3B]" />
              <h2 className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: "Barlow Condensed" }}>
                RELATED PLAYERS
              </h2>
              <span className="text-xs text-slate-500">Same era · same position</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {related.map((rp) => (
                <Link
                  key={rp.id}
                  to={`/players/${rp.slug}`}
                  className="group block bg-white border border-slate-200 rounded-lg overflow-hidden hover:border-[#7C2C3B] hover:shadow-md transition-all"
                  data-testid={`related-player-${rp.slug}`}
                >
                  <div className="relative h-28 bg-slate-100 overflow-hidden">
                    {rp.image_url ? (
                      <img
                        src={rp.image_url}
                        alt={rp.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <PlayerFallback className="w-full h-full" />
                    )}
                    <span className="absolute top-1.5 right-1.5 bg-[#7C2C3B] text-white text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide">
                      {rp.position || "Player"}
                    </span>
                  </div>
                  <div className="p-2.5">
                    <h3
                      className="text-sm font-bold text-[#0F172A] truncate group-hover:text-[#7C2C3B]"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      {rp.name?.toUpperCase()}
                    </h3>
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mt-0.5">
                      <span>{rp.years_active || "—"}</span>
                      <span className="text-[#2BAEE5] font-semibold">
                        {rp.appearances}g · {rp.goals}⚽
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8">
          <SocialShareButtons shareText={`${player.name} — West Ham United ${pos}`} />
        </div>
      </div>

      <Footer />
    </div>
  );
}
